// --------------------------------------------------------
// CARE LINE OCCUP HEALTH FIELD AREA - CareLineOccupHealthFieldArea
// --------------------------------------------------------

// --------------------------------------------------------
// GENERAL
// --------------------------------------------------------
import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../..';
import { initialObjectCareLineField, formats } from '../../constants/Objects';
import { insertElementIntoCareLine, removeFieldFromList, insertIntervaloOrFormatoIntoCareLine, buildFieldList } from '../../helpers/CareLineHelperFunctions';
import { generateIntervalObject } from '../../helpers/HelperFunctions';
import Category from '../../models/core/careLine/Category';
import Exam from '../../models/core/careLine/Exam';
import Procedure from '../../models/core/careLine/Procedure';
import VitalSign from '../../models/core/careLine/VitalSign';
import { FieldContainer } from '../../pages/careLine/CareLine.styled';
import CareLineAddArea from '../careLineAddArea/CareLineAddArea';
import { Text, H2 } from '../UI/generalLayout/Layout.styled';
import CareLineOccupHealthFieldButtonArea from './CareLineOccupHealthField';
import * as careLineActions from '../../store/actions/careLine';
import { CareLineFields } from '../../enums/CareLineFields';
import styled from 'styled-components';
import lodash from 'lodash';
import Colors from '../../constants/Colors';

// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
// --------------------------------------------------------

// --------------------------------------------------------
// CARE LINE OCCUP HEALTH FIELD AREA INTERFACE
// --------------------------------------------------------

interface ICareLineOccupHealthFieldArea{
    categories: any;
    categoryError?: string;
    setCategories: any;
    vitalSigns: any;
    vitalSignsError?: string;
    setVitalSigns: any;
    exams: any;
    examsError?: string;
    setExams: any;
    procedures: any;
    proceduresError?: string;
    setProcedures: any;
    isConfirmationState: boolean;
    isView: boolean;
    setNoFieldEditedOnEditState: any;
}

const ErrorText = styled(Text)`
    color: ${Colors.error};
    margin-top: -5px;
    margin-bottom: -5px;
`
/**
/**
 * A component that renders a Component description here.
 *
 * @param props The component props.
 * @returns The component JSX.
 */
const CareLineOccupHealthFieldArea = ({
    categories,
    setCategories,
    vitalSigns,
    setVitalSigns,
    exams,
    setExams,
    procedures,
    setProcedures,
    isConfirmationState,
    setNoFieldEditedOnEditState,
    isView,
    categoryError = '',
    vitalSignsError = '',
    examsError = '',
    proceduresError = ''
}:ICareLineOccupHealthFieldArea) => {
  // --------------------------------------------------------
  // CARE LINE OCCUP HEALTH FIELD AREA PRIVATE DECLARATIONS
  // --------------------------------------------------------

  const allExams: Exam[] = useSelector((state: any) => state.careLine.allExams)
  const allProcedures: Procedure[] = useSelector((state: any) => state.careLine.allProcedures)
  const allCategories: Category[] = useSelector((state: any) => state.careLine.allCategories)
  const allVitalSigns: VitalSign[] = useSelector((state: any) => state.careLine.allVitalSigns)
  const dispatch = useAppDispatch()

  const getDropdownData = useCallback(async () => {
    await dispatch(careLineActions.fetchDropdownData())
  }, [])

  useEffect(()=>{
    getDropdownData()
  }, [getDropdownData])

  const showFieldOnConfirmationState = (list: any, fieldName: string)=>{
    return !(isConfirmationState && buildFieldList(list, fieldName).length === 0)
  }

  const getIdsList = (list: any[])=>{
    const ids: string[] = [];
    list.forEach(item=>{
        ids.push(item.id)
    })
    return ids;
  }

  const isLastObjectFilledField = (hasIntervalo: boolean, hasFormato: boolean, list: any)=>{
    const lastObject = list[list.length > 0? list.length - 1 : 0]
    return lastObject.nome !== '' 
    && lastObject.id !== '' 
    && (hasFormato? !lodash.isEqual({}, lastObject.formato) : true)
    && (hasIntervalo? !lodash.isEqual({}, lastObject.intervaloMeses) : true);
  }
  
  const onChangeMainValue = (value: any, index: number, list: any, setCallback: any) => {
    setNoFieldEditedOnEditState(false); 
    setCallback([...insertElementIntoCareLine(list, value, index)]) 
  }

  const onRemoveFromList = (index: number, list: any, setCallback: any)=>{
    setNoFieldEditedOnEditState(false); 
    setCallback([...removeFieldFromList(list, index)])
  }


  const onChangeIntervaloOrFormato = (value: any, index: number, list: any, setCallback: any, isIntervalo: boolean)=>{
    setNoFieldEditedOnEditState(false); 
    setCallback([...insertIntervaloOrFormatoIntoCareLine(list, value, index, isIntervalo)])
  }

  // --------------------------------------------------------



  // --------------------------------------------------------
  // REACT RETURN FUNCTION
  // --------------------------------------------------------
  return (
    <>
        <FieldContainer>
            {categories.length > 0 && (<H2>Categoria*</H2>)}
            {categoryError !== '' && (<ErrorText>{categoryError}</ErrorText>)}
            {categories.map((category: any, index: number) => {
                return <CareLineAddArea
                error={categoryError !== ''}
                mainDropdownTopText={`Selecione a categoria do atendimento`}
                dropdownPlaceholder="Selecione a categoria"
                disabled={isConfirmationState || isView}
                mainValue={categories[index]}
                onChangeMainValue={(value: any) => { onChangeMainValue(value, index, categories, setCategories) }}
                mainValueItems={allCategories.filter((c: Category)=>{ return !getIdsList(categories).includes(c.id ?? '')})}
                intervalo={category.intervaloMeses === null ? '' : category.intervaloMeses}
                intervaloItems={generateIntervalObject()}
                onRemove={()=>{onRemoveFromList(index, categories, setCategories)}}
                showRemoveButton={categories.length > 1 && !isConfirmationState && !isView}
                onChangeIntervalo={(value: any) => { onChangeIntervaloOrFormato(value, index, categories, setCategories, true) }}
                />
            })
            }

            {categories.length > 0 && (<CareLineOccupHealthFieldButtonArea 
                onAddField={()=>{ 
                    setNoFieldEditedOnEditState(false); 
                    if(isLastObjectFilledField(true, false, categories)){
                        setCategories([...categories, initialObjectCareLineField]);
                    }
                }}
                disabled={isView? true : !isLastObjectFilledField(true, false, categories)}
                isConfirmationState={isConfirmationState}
                isViewState={isView} 
                addButtonText={'Adicionar Categoria'} 
                removeButtonText={'Remover Categoria'}  
            />)}
        </FieldContainer>

        {showFieldOnConfirmationState(vitalSigns, CareLineFields.SinalVital) && <FieldContainer>
            {vitalSigns.length > 0 && (<H2>Sinais Vitais</H2>)}
            {vitalSignsError !== '' && (<ErrorText>{vitalSignsError}</ErrorText>)}
            {vitalSigns.map((vitalSign: any, index: number) => {
            return <CareLineAddArea
                error={vitalSignsError !== ''}
                mainDropdownTopText="Selecione os tipos de sinais vitais"
                dropdownPlaceholder="Descrição dos sinais vitais"
                disabled={isConfirmationState || isView}
                mainValue={vitalSigns[index]}
                showRemoveButton={vitalSigns.length > 1 && !isConfirmationState && !isView}
                onRemove={()=>{onRemoveFromList(index, vitalSigns, setVitalSigns)}}
                onChangeMainValue={(value: any) => { onChangeMainValue(value, index, vitalSigns, setVitalSigns) }}
                mainValueItems={allVitalSigns.filter(v=>{ return !getIdsList(vitalSigns).includes(v.id ?? '')})}
            />
            })}

            {vitalSigns.length > 0 && (<CareLineOccupHealthFieldButtonArea 
                onAddField={() => { 
                    setNoFieldEditedOnEditState(false); 
                    if(isLastObjectFilledField(false, false, vitalSigns)){
                        setVitalSigns([...vitalSigns, initialObjectCareLineField]) }
                    }
                }
                isConfirmationState={isConfirmationState}
                isViewState={isView} 
                disabled={isView? true : !isLastObjectFilledField(false, false, vitalSigns)}
                addButtonText={'Adicionar Sinal Vital'} 
                removeButtonText={'Remover Sinal Vital'}  
            />)}                   
        </FieldContainer>}

        {showFieldOnConfirmationState(exams, CareLineFields.Exame) && <FieldContainer>
            {exams.length > 0 && (<H2>Exames</H2>)}
            {examsError !== '' && (<ErrorText>{examsError}</ErrorText>)}
            {exams.map((exam: any, index: number) => {
            return <CareLineAddArea
                error={examsError !== ''}
                mainDropdownTopText="Selecione os exames"
                dropdownPlaceholder="Selecione o tipo de exame"
                disabled={isConfirmationState || isView}
                mainValue={exams[index]}
                showRemoveButton={exams.length > 1 && !isConfirmationState && !isView}
                onRemove={()=>{onRemoveFromList(index, exams, setExams)}}
                onChangeMainValue={(value: any) => { onChangeMainValue(value, index, exams, setExams) }}
                mainValueItems={allExams.filter(e=>{ return !getIdsList(exams).includes(e.id ??'')})}
                intervalo={exam.intervaloMeses === null ? '' : exam.intervaloMeses}
                intervaloItems={generateIntervalObject()}
                onChangeIntervalo={(value: any) => { onChangeIntervaloOrFormato(value, index, exams, setExams, true) }}
                formato={exam.formato === null ? '' : exam.formato}
                formatoItems={formats}
                onChangeFormato={(value: any) => { onChangeIntervaloOrFormato(value, index, exams, setExams, false)  }}
            />
            })}

            {exams.length > 0 && (<CareLineOccupHealthFieldButtonArea 
                onAddField={() => { 
                    setNoFieldEditedOnEditState(false); 
                    if(isLastObjectFilledField(true, true, exams)){
                        setExams([...exams, initialObjectCareLineField]) 
                    }}}
                isConfirmationState={isConfirmationState}
                disabled={isView? true : !isLastObjectFilledField(true, true, exams)}
                isViewState={isView} 
                addButtonText={'Adicionar Exame'} 
                removeButtonText={'Remover Exame'}  
            />)}
        </FieldContainer>}

        {showFieldOnConfirmationState(procedures, CareLineFields.Procedimento) && <FieldContainer>
            {procedures.length > 0 && (<H2>Procedimentos</H2>)}
            {proceduresError !== '' && (<ErrorText>{proceduresError}</ErrorText>)}
            {procedures.map((procedure: any, index: number) => {
            return <CareLineAddArea
                error={proceduresError !== ''}
                mainDropdownTopText="Selecione os procedimentos"
                disabled={isConfirmationState || isView}
                dropdownPlaceholder="Selecione o procedimento"
                mainValue={procedures[index]}
                showRemoveButton={procedures.length > 1 && !isConfirmationState && !isView}
                onRemove={()=>{onRemoveFromList(index, procedures, setProcedures)}}
                onChangeMainValue={(value: any) => { onChangeMainValue(value, index, procedures, setProcedures) }}
                mainValueItems={allProcedures.filter(p=>{ return !getIdsList(procedures).includes(p.id ?? '')})}
                intervalo={procedure.intervaloMeses === null ? '' : procedure.intervaloMeses}
                intervaloItems={generateIntervalObject()}
                onChangeIntervalo={(value: any) => { onChangeIntervaloOrFormato(value, index, procedures, setProcedures, true) }}
            />
            })}

            {procedures.length > 0 && (<CareLineOccupHealthFieldButtonArea 
                onAddField={() => { 
                    setNoFieldEditedOnEditState(false); 
                    if(isLastObjectFilledField(true, false, procedures)){
                        setProcedures([...procedures, initialObjectCareLineField])
                    }}}
                disabled={isView? true : !isLastObjectFilledField(true, false, procedures)}
                isConfirmationState={isConfirmationState}
                isViewState={isView} 
                addButtonText={'Adicionar Procedimento'} 
                removeButtonText={'Remover Procedimento'}  
            />)}
        </FieldContainer>}

    </>
  )
  // --------------------------------------------------------
}

export default CareLineOccupHealthFieldArea